import { useState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { EPageSection } from '@/lib/enums';
import { cn, preserveQueryParams } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';

import { getTabStateClass } from './MenuItem';

interface IMenuSubItemProps {
  title: string;
  to?: string;
  icon?: React.ReactNode;
  isDisabled?: boolean;
  onClick?: () => void;
  isTopFixedHeader?: boolean;
  className?: string;
  'data-testid'?: string;
}

export const MenuSubItem: React.FC<IMenuSubItemProps> = ({
  title,
  to,
  icon,
  isDisabled = false,
  onClick,
  isTopFixedHeader = false,
  className,
  'data-testid': dataTestId,
}) => {
  const { setCurrentPageName } = useAppState();
  const pathname = usePathname();
  const [isSubPageHovered, setIsSubPageHovered] = useState(false);
  const isActive = pathname === to;
  const href = isDisabled || !to ? '' : preserveQueryParams(to);

  const handleMouseDown = (): void => {
    setCurrentPageName(title);
  };

  const handleClick = () => {
    onClick?.();
  };

  const fontWeightClass = isTopFixedHeader
    ? isActive
      ? 'font-black'
      : 'font-medium'
    : 'font-bold';

  return (
    <Link
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onMouseEnter={() => setIsSubPageHovered(true)}
      onMouseLeave={() => setIsSubPageHovered(false)}
      className={cn(
        `menu-sub-item text-xl ${fontWeightClass} flex w-full cursor-pointer items-center justify-between truncate rounded-[10px] px-[18px] py-2.5`,
        getTabStateClass(isActive, isSubPageHovered),
        className
      )}
      data-page-section={EPageSection.MENU_SUBITEM}
      data-testid={dataTestId}
      href={href}>
      <div className="flex min-w-0 items-center gap-x-3">
        {icon}
        <span className="menu-label truncate">{title}</span>
      </div>
    </Link>
  );
};